import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Tag, ModalYesNo, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { Button } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { Switch, Select } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useArchiveDoctor,
  useDoctorSetupDetails,
  useRolesList,
  useUpdateDoctor,
} from '@/settings/services';
import type { DoctorSetupDetail } from '@/types/settings-api';
import { EPrescriptionLoginsSection } from '../eprescription-logins-section';
import { useRegisterPractitionerSave } from '../use-practitioner-save-context';
import { PasswordChangeForm } from '@/settings/shared/password-change-form';
import { DisplayPreferencesSection } from '@/settings/shared/display-preferences-section';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practitioner Account Details tab — PROD-761 commit 2.
 *
 * Fully wired: `GET /api/Doctor/DoctorSetupDetails/{id}` → editable form →
 * `PUT /api/Doctor/{id}`. Plus eRx login editor section below the main
 * form: list per-practice rows, edit username + password inline, save
 * each via `POST /api/Doctor/AddUpdateEPrescriptionLogin`.
 *
 * Save model is per-tab (matches Flutter `doctor_base_widget.dart`).
 * Main save dispatches `useUpdateDoctor`; eRx rows save independently.
 *
 * Wire-shape preserved verbatim (from PR #107 docs):
 * - `role` is an integer enum
 * - `photoURL` uppercase URL — backend field name
 * - `doctorSpecialities[]` + `doctorSpecialityIds[]` are a parallel pair;
 *   v2 keeps them view-only (full SNOMED typeahead is PROD-788 follow-up)
 * - `isLastLogin` is SCRUBBED to null on PUT (PR #113 @samer review).
 *   The field is server-controlled — echoing it back is a PHI-audit
 *   smell. Backend ignores it on PUT either way. Matches the same
 *   scrub on Staff (PROD-765 PR #64 follow-up).
 * - Email is LOCKED on edit (account identity — matches Staff pattern)
 */
export interface PractitionerAccountTabProps {
  doctorSetupId: string;
  canEdit: boolean;
  /**
   * Show the "Archive This Practitioner" action (Flutter
   * `doctor_account_tab.dart:458`, gated on `Manage_Doctors` delete). The
   * owning page computes this — false in create mode and in Account Settings
   * self-edit (`accountMode`), where Flutter shows the simplified layout.
   */
  canArchive?: boolean;
  /** Called after a successful archive so the page can navigate away. */
  onArchived?: () => void;
  /**
   * Account Settings self-edit (Flutter `isFromAccountSettings`). When true,
   * render the SIMPLIFIED layout — Email (read-only) + password change + eRx
   * logins only — matching `doctor_account_tab.dart:56-137`. A user editing
   * their OWN record must NOT see account-active, role, mobile, specialities,
   * or archive.
   */
  accountMode?: boolean;
}

export function PractitionerAccountTab({
  doctorSetupId,
  canEdit,
  canArchive = false,
  onArchived,
  accountMode = false,
}: PractitionerAccountTabProps) {
  const { data, isLoading } = useDoctorSetupDetails(doctorSetupId);
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const selfUserId = useAuthStore((s) => s.user?.userId);
  const { data: roles = [] } = useRolesList(orgId);
  const updateMutation = useUpdateDoctor();

  const [source, setSource] = useState<DoctorSetupDetail | null>(null);
  const [draft, setDraft] = useState<DoctorSetupDetail | null>(null);

  // Seed source/draft from the loaded record by adjusting state *during render*
  // (React's "adjusting state when a prop changes" pattern) rather than in an
  // effect — avoids the synchronous-setState-in-effect cascading-render warning
  // (same fix as staff-edit-page.tsx, PROD-967). Re-seeds only when the loaded
  // record's id changes; a same-id refetch keeps in-flight edits.
  const [seededId, setSeededId] = useState<string | null>(null);
  const loaded = data?.doctorSetupDetail ?? null;
  if (loaded && seededId !== doctorSetupId) {
    setSeededId(doctorSetupId);
    setSource(loaded);
    setDraft(loaded);
  }

  const isDirty = useMemo(() => {
    if (!source || !draft) return false;
    return JSON.stringify(source) !== JSON.stringify(draft);
  }, [source, draft]);

  const isValid =
    !!draft &&
    draft.firstName.trim().length > 0 &&
    draft.lastName.trim().length > 0 &&
    draft.role !== null;

  const handleSave = useCallback(() => {
    if (!draft) return;
    if (!isValid) {
      toast.error('Please complete the required fields.');
      return;
    }
    // SCRUB isLastLogin to null on PUT — server-controlled field, defense
    // in depth. Mirrors the same scrub applied to Staff in PROD-765
    // (@samer review on PR #64 + reaffirmed on PR #113). The backend
    // ignores the field on PUT, but echoing a session-marker timestamp
    // back is a PHI-audit smell that's cheap to avoid.
    const payload = { ...draft, isLastLogin: null };
    updateMutation.mutate(payload, {
      onSuccess: () => toast.success('Account details saved'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Save failed'),
    });
  }, [draft, isValid, updateMutation]);

  // PROD-897: register this tab's save with the page chrome so the
  // page-level Save button dispatches the same `handleSave`. Inline
  // button below remains the primary affordance; the chrome button
  // is an alternate access point per Flutter `doctor_base_widget.dart`.
  // Called unconditionally before the early-return — Rules of Hooks.
  useRegisterPractitionerSave(
    canEdit && draft && !accountMode
      ? {
          canSave: isDirty && isValid,
          isPending: updateMutation.isPending,
          save: handleSave,
          label: updateMutation.isPending ? 'Saving…' : 'Save Account Details',
        }
      : null,
  );

  if (isLoading || !draft) {
    return <BrandLoading size={48} label="Loading…" />;
  }

  // Account Settings self-edit — simplified layout (Flutter parity): email,
  // password change, and eRx logins only.
  if (accountMode) {
    return (
      <div className="space-y-6">
        {/* The viewer's own display preferences, first (PM's Display card; PROD-3313). */}
        <DisplayPreferencesSection />
        <h2 className={SETTINGS_SECTION_TITLE}>
          Account details
        </h2>
        <PlainCard>
          <div>
            <FieldLabel stacked>Email address</FieldLabel>
            <TextField type="email" value={draft.emailId} disabled readOnly />
          </div>
        </PlainCard>

        <h2 className={SETTINGS_SECTION_TITLE}>
          Password
        </h2>
        <PlainCard>
          <PasswordChangeForm userId={selfUserId} />
        </PlainCard>

        <EPrescriptionLoginsSection providerId={doctorSetupId} canEdit={canEdit} />
      </div>
    );
  }

  const setField = <K extends keyof DoctorSetupDetail>(
    key: K,
    value: DoctorSetupDetail[K],
  ) => {
    setDraft({ ...draft, [key]: value });
  };

  const usableRoles = roles.filter((r) => !r.isArchived && r.roleId != null);

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Account status
      </h2>
      <PlainCard>
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-default">Set account active</span>
          <Switch
            checked={draft.isActive}
            disabled={!canEdit}
            onCheckedChange={(next) => setField('isActive', next)}
            aria-label="Set account active"
          />
        </div>
        <p className="text-sm text-text-muted">
          Enabling &lsquo;Set account active&rsquo; will let the user log in into the
          system. We recommend keeping the account active at all times as long
          as the user is still a member of your organisation.
        </p>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Account details
      </h2>
      <PlainCard>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>
              First name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.firstName}
              disabled={!canEdit}
              onChange={(e) => setField('firstName', e.target.value)}
            />
          </div>
          <div>
            <FieldLabel stacked>
              Last name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.lastName}
              disabled={!canEdit}
              onChange={(e) => setField('lastName', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>
              Role <span className="text-status-error">*</span>
            </FieldLabel>
            <Select
              value={draft.role != null ? String(draft.role) : undefined}
              disabled={!canEdit}
              onChange={(next) =>
                setField('role', next ? Number(next) : null)
              }
              placeholder="Select a role"
              options={usableRoles.map((r) => ({
                label: r.roleName ?? `Role ${r.roleId}`,
                value: String(r.roleId),
              }))}
            />
          </div>
          <div>
            <FieldLabel stacked>
              Email address <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              type="email"
              value={draft.emailId}
              disabled
              readOnly
            />
            <p data-slot="settings-field-helper" className="mt-1 text-xs text-text-muted">
              Email cannot be changed after the account is created.
            </p>
          </div>
        </div>

        <div>
          <FieldLabel stacked>Mobile number</FieldLabel>
          <CountryDialPicker
            value={draft.mobileNumber ?? ''}
            disabled={!canEdit}
            onChange={(v) => setField('mobileNumber', v)}
          />
        </div>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Specialities
      </h2>
      <PlainCard>
        {draft.doctorSpecialities.length === 0 ? (
          <p className="text-sm text-text-muted">
            No specialities assigned. Add/remove UI pending the SNOMED
            value-set lookup endpoint (shared with PROD-788 Staff Professional).
          </p>
        ) : (
          <div>
            <div className="flex flex-wrap gap-2">
              {draft.doctorSpecialities.map((s) => (
                <Tag key={s.specialitySkillId}>
                  {s.code.displayName ?? 'Speciality'}
                </Tag>
              ))}
            </div>
            <p className="mt-2 text-xs text-text-muted">
              Specialities are preserved from the existing record. Add/remove
              UI pending SNOMED lookup endpoint.
            </p>
          </div>
        )}
      </PlainCard>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || !isValid || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save Account Details'}
          </Button>
        </div>
      )}

      <EPrescriptionLoginsSection
        providerId={doctorSetupId}
        canEdit={canEdit}
      />

      {canArchive && doctorSetupId && (
        <ArchivePractitionerSection
          doctorSetupId={doctorSetupId}
          onArchived={onArchived}
        />
      )}
    </div>
  );
}

/**
 * Archive This Practitioner — Flutter parity (`doctor_account_tab.dart:454-478`).
 * Only rendered for an existing practitioner with `Manage_Doctors` delete
 * permission (the page gates via `canArchive`). Uses shadcn `AlertDialog` for
 * the destructive confirm — NOT `window.confirm` (blocked in embedded WebView
 * contexts, CONTRIBUTING recidivism log PR #228). Title / message / button copy
 * copied verbatim from the Flutter literals.
 */
function ArchivePractitionerSection({
  doctorSetupId,
  onArchived,
}: {
  doctorSetupId: string;
  onArchived?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const archive = useArchiveDoctor();

  const handleArchive = () => {
    archive.mutate(doctorSetupId, {
      onSuccess: () => {
        toast.success('Practitioner archived');
        setOpen(false);
        onArchived?.();
      },
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : 'Could not archive this practitioner.',
        ),
    });
  };

  return (
    <PlainCard>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-text-muted">
          Archiving removes this practitioner&rsquo;s access. You can unarchive
          the account later from the archived list.
        </p>
        <Button
          type="button"
          variant="tertiary"
          onClick={() => setOpen(true)}
          className="shrink-0 text-status-error hover:text-status-error hover:bg-status-error/10"
        >
          Archive This Practitioner
        </Button>
      </div>

      <ModalYesNo
      open={open}
      onCancel={() => setOpen(false)}
      title="Do you really want to archive this account?"
      message="We will inform the practitioner about this change. You can
              unarchive this account later in Account Settings."
      cancelLabel="Cancel"
      confirmLabel={archive.isPending ? 'Archiving…' : 'Archive This Practitioner'}
      onConfirm={handleArchive}
      loading={archive.isPending}
    />
    </PlainCard>
  );
}

function PlainCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}
